import React, { useCallback, useContext, useState, createRef, useEffect, useRef } from "react";
import { connect } from "react-redux";

import { bindActionCreators } from "@reduxjs/toolkit";
import { Formik } from "formik";
import { Temporal } from "js-joda";
import nameof from "ts-nameof.macro";

import { Form, Dialog, CancelLink, Text, Checkbox, DataColumnDef, PickerInput, OkButton, Banner as PlexBanner, InputSize, DateTimeInput } from "@plex/react-components";

import { IKeyData } from "infrastructure";
import { INotification, notification } from "infrastructure/notification";
import { IValidatable, validate } from "infrastructure/validation";
import { Banner } from "ui/app";
import { DialogContainer, DialogNavigationContext } from "ui/app/navigation";
import { ValidatableElement } from "ui/components";
import { useLocalizedValues, useLocalizerWithFallback } from "ui/hooks";
import {
  IAccountsList,
  IAccount,
  accountActions,
  ITenantsState,
  IAccountsState,
  ITenantsList,
  tenantActions,
  ITenant,
  ITenantSearchCriterion,
  IAccountSearchCriterion,
  ISecurityRole,
  IEnterprise
} from "ui/screens/security/models";
import { securityMessages } from "ui/screens/security/security.localization";

import { securityRoleMembersActions } from "../SecurityRoleMembers.actions";
import styles from "../SecurityRoleMembers.module.scss";

import { getGrantRoleData } from "./GrantRole.helpers";
import {
  IGrantRoleFormState,
  IGrantRoleData,
  tenantPickerName,
  validDateFromPickerName,
  validDateToPickerName,
  accountPickerName,
  enterpriseWideFieldName
} from "./GrantRole.state";
import { getGrantRoleFormState, dateRangeRequired } from "./GrantRole.validation";

import { ISecurityRoleMembersState } from "..";

interface IGrantRoleProps {
  securityRole: ISecurityRole;
  enterprise?: IEnterprise;
  tenant?: ITenant;
  accounts: IAccountsList;
  tenants: ITenantsList;
  grantRoleCompleted?: boolean;
  notifications: INotification[];
  loadTenants: (criterion: IKeyData<ITenantSearchCriterion>) => void;
  loadAccounts: (criterion: IKeyData<IAccountSearchCriterion>) => void;
  clearAccounts: (criterion: IKeyData) => void;
  grantRole: (grantRoleData: IGrantRoleData) => void;
  resetGrantRoleProcess: () => void;
}

const ValidationSummary: React.FC<{
  track: boolean;
  isValid: boolean;
  notification?: INotification;
  hideNotification: () => void;
  createNotification: () => void;
  stopTrack: () => void;
}> = ({ isValid, hideNotification, notification, createNotification, track, stopTrack }) => {
  const localizeWithFallback = useLocalizerWithFallback();

  const stopTrackRef = useRef(stopTrack);
  const createNotificationRef = useRef(createNotification);
  const hideNotificationRef = useRef(hideNotification);

  useEffect(() => {
    if (isValid && notification) {
      hideNotificationRef.current?.();
    } else if (!isValid && !notification && track) {
      createNotificationRef.current?.();
    } else if (track) {
      stopTrackRef.current?.();
    }
  }, [isValid, notification, track]);

  return !isValid && notification ? (
    <PlexBanner close={() => hideNotification()} status={notification.type}>
      {localizeWithFallback(notification.content[0]?.message)}
    </PlexBanner>
  ) : null;
};

export const GrantRole: React.FC<IGrantRoleProps> = ({
  accounts,
  tenants,
  securityRole,
  tenant,
  enterprise,
  loadAccounts,
  loadTenants,
  clearAccounts,
  notifications,
  grantRole,
  grantRoleCompleted,
  resetGrantRoleProcess
}) => {
  const dialogNavigationContext = useContext(DialogNavigationContext);

  const localizeWithFallback = useLocalizerWithFallback();

  const localizedValues = useLocalizedValues(() => ({
    grantRoleTitle: securityMessages.title("grantRole"),
    securityRoleLabel: securityMessages.heading("securityRoleField"),
    enterpriseLabel: securityMessages.heading("enterpriseField"),
    tenantLabel: securityMessages.heading("tenantField"),
    accountLabel: securityMessages.heading("accountField"),
    validDateFromLabel: securityMessages.heading("validDateFromField"),
    validDateToLabel: securityMessages.heading("validDateToField"),
    enterpriseWideLabel: securityMessages.heading("enterpriseWideField")
  }));

  const [initialFormState, setInitialFormState] = useState<IGrantRoleFormState>(getGrantRoleFormState());
  const [trySubmit, setTrySubmit] = useState(false);

  const [validationNotification, setValidationNotification] = useState<INotification | undefined>();

  const close = useCallback(() => {
    dialogNavigationContext.hideDialog(undefined, () => {
      resetGrantRoleProcess();
      clearAccounts({ id: accountPickerName } as IKeyData);
    });
  }, [dialogNavigationContext, resetGrantRoleProcess, clearAccounts]);

  const formRef = createRef<Formik<IGrantRoleFormState>>();

  const onSubmitClick = useCallback(async () => {
    setTrySubmit(true);
    await formRef.current?.submitForm();
  }, [formRef]);

  const onPickerValueChanged = <T, P extends keyof IGrantRoleFormState>(
    property: P,
    selected: T[],
    values: IGrantRoleFormState,
    setValues: (values: IGrantRoleFormState) => void,
    onUpdate?: (values: IGrantRoleFormState) => void
  ) => {
    const propertyName = property as string;

    const updatedValues = {
      ...values,
      [propertyName]: {
        ...values[propertyName],
        value: {
          selectedItems: selected
        }
      }
    };

    setValues(updatedValues);

    onUpdate?.(updatedValues);
  };

  const onValueChanged = <T, P extends keyof IGrantRoleFormState>(property: P, value: T, values: IGrantRoleFormState, setValues: (values: IGrantRoleFormState) => void) => {
    const propertyName = property as string;

    setValues({
      ...values,
      [propertyName]: {
        ...values[propertyName],
        value: value
      }
    });
  };

  useEffect(() => {
    if (tenant) {
      setInitialFormState(prev => ({ ...prev, [tenantPickerName]: { ...prev[tenantPickerName], value: { selectedItems: [tenant], isLoaded: true, initialized: true } } }));
    }
  }, [tenant]);

  useEffect(() => {
    if (notifications.length) {
      setTrySubmit(false);
    }
  }, [notifications]);

  useEffect(() => {
    if (grantRoleCompleted) {
      close();
    }
  }, [grantRoleCompleted, close]);

  return (
    <DialogContainer
      render={dialogProps => {
        return (
          <Dialog {...dialogProps} minWidth={500} title={localizedValues.grantRoleTitle}>
            <Dialog.Body>
              <Formik
                initialValues={initialFormState}
                enableReinitialize
                ref={formRef}
                validate={values => {
                  return validate((values as unknown) as { [x: string]: IValidatable<any> });
                }}
                onSubmit={values => {
                  grantRole(getGrantRoleData(values, securityRole));
                }}
              >
                {({ values, touched, handleBlur, handleSubmit, isValid, setValues, validateField, setFieldTouched }) => (
                  <Form onSubmit={handleSubmit}>
                    <Form.Header>
                      <ValidationSummary
                        track={trySubmit}
                        createNotification={() => setValidationNotification(notification.error().useMessageId(securityMessages.error("grantRoleValidationFailed")))}
                        isValid={isValid}
                        hideNotification={() => setValidationNotification(undefined)}
                        notification={validationNotification}
                        stopTrack={() => setTrySubmit(false)}
                      />
                      <Banner messages={notifications} />
                    </Form.Header>
                    <Form.Body>
                      <Form.Row>
                        <Form.FieldPair labelText={localizedValues.securityRoleLabel} className={styles.formField}>
                          <div className={styles.grantRoleFormField}>
                            <Text text={localizeWithFallback({ value: securityRole.name })} />
                          </div>
                        </Form.FieldPair>
                      </Form.Row>
                      {enterprise && !tenant ? (
                        <>
                          <Form.Row>
                            <Form.FieldPair labelText={localizedValues.enterpriseLabel} className={styles.formField}>
                              <div className={styles.grantRoleFormField}>
                                <Text text={enterprise?.name} />
                              </div>
                            </Form.FieldPair>
                          </Form.Row>
                          <Form.Row>
                            <Form.FieldPair labelText={localizedValues.tenantLabel} required={enterprise && !tenant} className={styles.formField}>
                              {
                                <ValidatableElement
                                  value={values[tenantPickerName].value}
                                  validation={{
                                    hideError: !touched[tenantPickerName],
                                    validationIssue: values[tenantPickerName]?.validation.result && { message: values[tenantPickerName]?.validation.result?.message }
                                  }}
                                  render={(value, props) => (
                                    <PickerInput
                                      size={InputSize.medium}
                                      dialogTitle={localizedValues.tenantLabel}
                                      selected={value.selectedItems}
                                      onBlur={handleBlur}
                                      name={tenantPickerName}
                                      errorMessage={localizeWithFallback(tenants?.notification?.content[0]?.message)}
                                      onSelectionChanged={(rows: ITenant[]) =>
                                        onPickerValueChanged(tenantPickerName, rows, values, setValues, updatedValues => {
                                          onPickerValueChanged(accountPickerName, [], updatedValues, setValues);
                                          clearAccounts({ id: accountPickerName } as IKeyData);
                                        })
                                      }
                                      keySelector={(row: ITenant) => row.tenantId}
                                      displaySelector={(row: ITenant) => row.tenantName}
                                      data={tenants?.items}
                                      onSearch={(query, cols) =>
                                        loadTenants({ id: tenantPickerName, data: { enterpriseId: enterprise?.enterpriseId, query: query, searchedColumns: cols } })
                                      }
                                      {...props}
                                    >
                                      <DataColumnDef<ITenant> id="tenantName" title={localizedValues.tenantLabel} valueSelector={row => row.tenantName} searchable>
                                        {row => row.tenantName}
                                      </DataColumnDef>
                                    </PickerInput>
                                  )}
                                />
                              }
                            </Form.FieldPair>
                          </Form.Row>
                        </>
                      ) : (
                        <Form.Row>
                          <Form.FieldPair labelText={localizedValues.tenantLabel} className={styles.formField}>
                            <div className={styles.grantRoleFormField}>
                              <Text text={values[tenantPickerName].value.selectedItems[0]?.tenantName} />
                            </div>
                          </Form.FieldPair>
                        </Form.Row>
                      )}
                      <Form.Row>
                        <Form.FieldPair labelText={localizedValues.accountLabel} required className={styles.formField}>
                          <ValidatableElement
                            value={values[accountPickerName].value}
                            validation={{
                              hideError: !touched[accountPickerName],
                              validationIssue: values[accountPickerName]?.validation.result && { message: values[accountPickerName]?.validation.result?.message }
                            }}
                            render={(value, props) => (
                              <PickerInput
                                size={InputSize.medium}
                                dialogTitle={localizedValues.accountLabel}
                                name={accountPickerName}
                                onBlur={handleBlur}
                                selected={value.selectedItems}
                                errorMessage={localizeWithFallback(accounts?.notification?.content[0]?.message)}
                                onSelectionChanged={(rows: IAccount[]) => onPickerValueChanged(accountPickerName, rows, values, setValues)}
                                keySelector={(row: IAccount) => row.accountId}
                                displaySelector={(row: IAccount) => row.accountName}
                                disabled={!values[tenantPickerName].value.selectedItems[0]?.tenantId}
                                data={accounts?.items}
                                onSearch={(query, cols) =>
                                  loadAccounts({
                                    id: accountPickerName,
                                    data: {
                                      tenantId: values[tenantPickerName].value.selectedItems[0].tenantId,
                                      query: query,
                                      searchedColumns: cols,
                                      securityRoleId: securityRole.securityRoleId
                                    }
                                  })
                                }
                                {...props}
                              >
                                .
                                <DataColumnDef<IAccount> id="accountName" title={localizedValues.accountLabel} valueSelector={row => row.accountName} searchable>
                                  {row => row.accountName}
                                </DataColumnDef>
                              </PickerInput>
                            )}
                          />
                        </Form.FieldPair>
                      </Form.Row>
                      <Form.Row>
                        <Form.FieldPair
                          labelText={localizedValues.validDateFromLabel}
                          required={dateRangeRequired(values[accountPickerName].value.selectedItems[0], values[tenantPickerName].value.selectedItems[0])}
                          className={styles.formField}
                        >
                          <ValidatableElement
                            value={values[validDateFromPickerName].value}
                            validation={{
                              hideError: !touched[validDateFromPickerName],
                              validationIssue: values[validDateFromPickerName]?.validation.result && { message: values[validDateFromPickerName]?.validation.result?.message }
                            }}
                            render={(value, props) => (
                              <DateTimeInput
                                precision={"minutes"}
                                size={InputSize.medium}
                                onBlur={handleBlur}
                                value={value}
                                name={validDateFromPickerName}
                                {...props}
                                onValueChange={(v: Temporal | null) => {
                                  onValueChanged(validDateFromPickerName, v, values, setValues);
                                  setFieldTouched(validDateFromPickerName, true);
                                  validateField(validDateFromPickerName);
                                }}
                              />
                            )}
                          />
                        </Form.FieldPair>
                      </Form.Row>
                      <Form.Row>
                        <Form.FieldPair
                          labelText={localizedValues.validDateToLabel}
                          required={dateRangeRequired(values[accountPickerName].value.selectedItems[0], values[tenantPickerName].value.selectedItems[0])}
                          className={styles.formField}
                        >
                          <ValidatableElement
                            value={values[validDateToPickerName].value}
                            validation={{
                              hideError: !touched[validDateToPickerName],
                              validationIssue: values[validDateToPickerName]?.validation.result && { message: values[validDateToPickerName]?.validation.result?.message }
                            }}
                            render={(value, props) => (
                              <DateTimeInput
                                precision={"minutes"}
                                size={InputSize.medium}
                                onBlur={handleBlur}
                                {...props}
                                value={value}
                                name={validDateToPickerName}
                                onValueChange={(v: Temporal | null) => {
                                  onValueChanged(validDateToPickerName, v, values, setValues);
                                  setFieldTouched(validDateToPickerName, true);
                                  validateField(validDateToPickerName);
                                }}
                              />
                            )}
                          />
                        </Form.FieldPair>
                      </Form.Row>
                      <Form.Row>
                        <Form.FieldPair labelText={localizedValues.enterpriseWideLabel} className={styles.formField}>
                          <Checkbox
                            checked={values[enterpriseWideFieldName].value}
                            onChange={e => onValueChanged(enterpriseWideFieldName, e.currentTarget.checked, values, setValues)}
                          />
                        </Form.FieldPair>
                      </Form.Row>
                    </Form.Body>
                  </Form>
                )}
              </Formik>
            </Dialog.Body>
            <Dialog.Footer>
              <CancelLink onClick={close} />
              <OkButton onClick={onSubmitClick} />
            </Dialog.Footer>
          </Dialog>
        );
      }}
    />
  );
};

export default connect(
  (state: { securityRoleMembers: ISecurityRoleMembersState; tenants: ITenantsState; accounts: IAccountsState }) => {
    return {
      tenants: state.tenants[tenantPickerName] || { items: [] },
      accounts: state.accounts[accountPickerName] || { items: [] },
      notifications: state.securityRoleMembers.notifications.filter(e => e.areaName === nameof<IGrantRoleFormState>()),
      grantRoleCompleted: state.securityRoleMembers.grantRoleCompleted
    } as Pick<IGrantRoleProps, "tenants" | "accounts" | "notifications" | "grantRoleCompleted">;
  },
  dispatch => {
    return bindActionCreators(
      {
        grantRole: securityRoleMembersActions.grantRole,
        resetGrantRoleProcess: securityRoleMembersActions.resetGrantRoleProcess,
        loadAccounts: accountActions.loadAccounts,
        clearAccounts: accountActions.clearAccounts,
        loadTenants: tenantActions.loadTenants
      } as Pick<IGrantRoleProps, "grantRole" | "resetGrantRoleProcess" | "loadAccounts" | "clearAccounts" | "loadTenants">,
      dispatch
    );
  }
)(GrantRole);
